import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { Textstyle } from "@/types/legend.types";
import type { Point3D } from "../types/types";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";
import { ensureDimensionStyle } from "./style";
import { dimensionRotation, type DimensionKind } from "./geometry";

export interface DimensionParams {
  kind: DimensionKind;
  /** First measured point. */
  a: Point3D;
  /** Second measured point. */
  b: Point3D;
  /** Any point of the dimension line. */
  dimLinePoint: Point3D;
  /** Height of the dimension text, in drawing units (= DIMSCALE). */
  textHeight: number;
  /** Font of the dimension text; the style default when absent. */
  textstyle?: Textstyle;
}

/**
 * Create a linear dimension in the current space. The caller owns the returned
 * id (undo command, selection) and must deleteObj() it.
 */
export function createDimension(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  params: DimensionParams,
): DrawingModule.OdDbObjectId {
  const styleId = ensureDimensionStyle(module, db, params.textstyle);

  const dimension =
    params.kind === "aligned"
      ? module.OdDbAlignedDimension.createObject()
      : module.OdDbRotatedDimension.createObject();
  dimension.setDatabaseDefaults(db, true);

  const a = new module.OdGePoint3d(params.a.x, params.a.y, 0);
  const b = new module.OdGePoint3d(params.b.x, params.b.y, 0);
  const dimLine = new module.OdGePoint3d(
    params.dimLinePoint.x,
    params.dimLinePoint.y,
    0,
  );
  dimension.setXLine1Point(a);
  dimension.setXLine2Point(b);
  dimension.setDimLinePoint(dimLine);
  deleteObj(a);
  deleteObj(b);
  deleteObj(dimLine);

  if (params.kind !== "aligned") {
    const rotated = module.OdDbRotatedDimension.cast(dimension);
    rotated.setRotation(dimensionRotation(params.kind));
    deleteObj(rotated);
  }

  dimension.setDimensionStyle(styleId);
  dimension.setDimscale(params.textHeight);
  deleteObj(styleId);

  const currentSpace = openAs(
    getCurrentSpaceId(db),
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(dimension);
  // The dimension block (lines, arrows, text) is only built on demand: without
  // this the entity exists but draws nothing until the next regen.
  dimension.recomputeDimBlock(true);
  noteGraphicsModified("Dimension");

  deleteObj(currentSpace);
  deleteObj(dimension);

  return entityId;
}

/**
 * Read the definition points of a linear dimension. A rotated dimension is
 * read as horizontal unless it is vertical: one at another angle (from a CAD
 * software) only gets an approximate handle position.
 */
export function getDimensionPoints(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): { kind: DimensionKind; a: Point3D; b: Point3D; dimLinePoint: Point3D } | null {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForRead);
  let rotated: DrawingModule.OdDbRotatedDimension | null = null;
  let aligned: DrawingModule.OdDbAlignedDimension | null = null;
  try {
    rotated = module.OdDbRotatedDimension.cast(entity) || null;
    aligned = rotated ? null : module.OdDbAlignedDimension.cast(entity) || null;
    const dimension = rotated ?? aligned;
    if (!dimension) return null;

    const read = (point: DrawingModule.OdGePoint3d): Point3D => {
      const result = { x: point.x, y: point.y, z: point.z };
      deleteObj(point);
      return result;
    };
    const kind: DimensionKind = rotated
      ? isVerticalRotation(rotated.rotation())
        ? "vertical"
        : "horizontal"
      : "aligned";
    return {
      kind,
      a: read(dimension.xLine1Point()),
      b: read(dimension.xLine2Point()),
      dimLinePoint: read(dimension.dimLinePoint()),
    };
  } finally {
    if (rotated) deleteObj(rotated);
    if (aligned) deleteObj(aligned);
    deleteObj(entity);
  }
}

/** Move the dimension line of a linear dimension and rebuild its block. */
export function setDimensionLinePoint(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  dimLinePoint: Point3D,
): void {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForWrite);
  let dimension:
    | DrawingModule.OdDbRotatedDimension
    | DrawingModule.OdDbAlignedDimension
    | null = null;
  try {
    dimension =
      module.OdDbRotatedDimension.cast(entity) ||
      module.OdDbAlignedDimension.cast(entity) ||
      null;
    if (!dimension) return;

    const point = new module.OdGePoint3d(dimLinePoint.x, dimLinePoint.y, 0);
    dimension.setDimLinePoint(point);
    deleteObj(point);

    dimension.recomputeDimBlock(true);
    entity.recordGraphicsModified(true);
    noteGraphicsModified("Dimension");
  } finally {
    if (dimension) deleteObj(dimension);
    deleteObj(entity);
  }
}

/**
 * Text of a dimension: the distance it measures, and the text the user forced
 * in its place (empty when the measured value is shown).
 */
export function getDimensionText(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): { measurement: number; forcedText: string } | null {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForRead);
  let dimension: DrawingModule.OdDbDimension | null = null;
  try {
    dimension = module.OdDbDimension.cast(entity) || null;
    if (!dimension) return null;
    const textStr = dimension.dimensionText();
    const forcedText = textStr.toString();
    deleteObj(textStr);
    return { measurement: dimension.measurement(), forcedText };
  } finally {
    if (dimension) deleteObj(dimension);
    deleteObj(entity);
  }
}

/**
 * Force the text of a dimension, or show the measured value again with an
 * empty text. "<>" in the text stands for the measured value (DWG rule), so
 * "<> env." keeps it with a suffix.
 */
export function setDimensionText(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  text: string,
): void {
  const entity = openAs(entityId, module.OdDbEntity, OpenMode.kForWrite);
  let dimension: DrawingModule.OdDbDimension | null = null;
  try {
    dimension = module.OdDbDimension.cast(entity) || null;
    if (!dimension) return;

    const textStr = new module.OdString();
    textStr.set(text);
    dimension.setDimensionText(textStr);
    deleteObj(textStr);

    dimension.recomputeDimBlock(true);
    entity.recordGraphicsModified(true);
    noteGraphicsModified("Dimension");
  } finally {
    if (dimension) deleteObj(dimension);
    deleteObj(entity);
  }
}

function isVerticalRotation(rotation: number): boolean {
  return Math.abs(Math.abs(Math.sin(rotation)) - 1) < 1e-6;
}
